import React from "react";
import { observer } from "mobx-react";
import { computed, observable } from "mobx";
import { FieldRenderProps } from "react-final-form";
import { ValueType } from "react-select";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";

import AddIcon from "@material-ui/icons/Add";

import { Button, FormLabel } from "@material-ui/core";

import Popup from "components/Popup";
import withFieldControl from "components/FormControl/FieldControl";
import Placeholder from "components/Placeholder";

import { reorder } from "utils/calculation";

import { DictionaryItem } from "constants/models";

import {
  Container,
  ControlHeader,
  ControlContainer,
  OptionCards,
  Row,
} from "./style";
import QuestionCard from "./QuestionCard";
import QuestionForm from "./QuestionForm/QuestionForm";
import QuestionStore from "../../../../../../stores/QuestionStore";
import QuestionnaireQuizStore from "../../../../../../stores/QuestionnaireQuizStore";

type Props = {
  quizID?: number;
};

@observer
class QuestionControl extends React.Component<FieldRenderProps<any> & Props> {
  @observable isPopupOpen: boolean = false;

  @computed
  get values() {
    const { input } = this.props;
    const value = input?.value || [];

    return value.map((item: any) => ({ label: item.title, value: item }));
  }

  @computed get filteredOptions() {
    return QuestionStore.questionOptions.filter(
      option => !this.values.find(({ value }) => value.id === option.value.id),
    );
  }

  onChange = (items: DictionaryItem[] = []) => {
    const {
      input: { onChange },
    } = this.props;

    const value = items.map(item => item.value);
    const event: any = { target: { value } };
    onChange(event);
  };

  onRemoveItem = (option: ValueType<DictionaryItem>) => async () => {
    const values = this.values.filter(
      item => item.value.id !== option.value.id,
    );
    this.onChange(values);
    const payload = {
      id: this.props.quizID,
      questionID: option.value.id,
    };
    await QuestionnaireQuizStore.deleteQuestion(payload);
    await QuestionnaireQuizStore.fetchQuizDetails(this.props.quizID);
  };

  closePopup = () => {
    this.isPopupOpen = false;
  };

  openPopup = () => {
    this.isPopupOpen = true;
  };

  saveNewMovements = async (newQuestions: DictionaryItem[]) => {
    try {
      const addQuestion = newQuestions.map((q: any) => ({
        questionID: q.value.id,
        displayOrder: 1,
      }));
      await QuestionnaireQuizStore.addQuestionToQuiz({
        addQuestion,
        id: this.props.quizID,
      });
    } catch (error) {
      console.log(`Error >> ${error}`);
    } finally {
      this.closePopup();
      QuestionnaireQuizStore.fetchQuizDetails(this.props.quizID);
    }
  };

  onDragEnd = async (result: DropResult) => {
    // dropped outside the list
    if (
      !result.destination ||
      !result.source ||
      result.source.index === result.destination.index
    ) {
      return;
    }

    const startIndex = result.source.index;
    const endIndex = result.destination.index;

    const reorderedQuestions = reorder(this.values, startIndex, endIndex);
    const newOrder: any[] = [];
    reorderedQuestions.map(item => newOrder.push({ id: item.value.id }));
    const payload = {
      newOrder,
      id: this.props.quizID,
    };
    this.onChange(reorderedQuestions);
    try {
      await QuestionnaireQuizStore.updateQuestionOrder(payload);
    } catch (error) {
      console.log(`Error >> ${error.message}`);
    } finally {
      await QuestionnaireQuizStore.fetchQuizDetails(this.props.quizID);
    }
  };

  render() {
    return (
      <>
        {this.isPopupOpen && (
          <Popup
            onClose={this.closePopup}
            canCloseByOuterClick={false}
            withoutExitButton
          >
            <QuestionForm
              options={this.filteredOptions}
              onClose={this.closePopup}
              onSave={this.saveNewMovements}
            />
          </Popup>
        )}
        <Container>
          <Row>
            <ControlHeader>
              <FormLabel>Questions</FormLabel>
              <ControlContainer>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  startIcon={<AddIcon />}
                  onClick={this.openPopup}
                >
                  Add Question
                </Button>
              </ControlContainer>
            </ControlHeader>
          </Row>

          {this.values.length ? (
            <Row>
              <OptionCards>
                <DragDropContext onDragEnd={this.onDragEnd}>
                  <Droppable droppableId="droppable">
                    {(provided, snapshot) => (
                      <div {...provided.droppableProps} ref={provided.innerRef}>
                        {this.values.map((item, index) => (
                          <Draggable
                            key={item.value.id}
                            draggableId={item.value.id.toString()}
                            index={index}
                          >
                            {(provided, snapshot) => (
                              <QuestionCard
                                key={item.value.id}
                                title={item.label}
                                onRemove={this.onRemoveItem(item)}
                                dragHandleProps={provided.dragHandleProps}
                                innerRef={provided.innerRef}
                                isDragging={snapshot.isDragging}
                                {...provided.draggableProps}
                                isEditMode
                              />
                            )}
                          </Draggable>
                        ))}

                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </OptionCards>
            </Row>
          ) : (
            <Placeholder
              height={132}
              width={132}
              text={"No movements added yet."}
            />
          )}
        </Container>
      </>
    );
  }
}

export default withFieldControl(QuestionControl);
